import {Observable} from 'rxjs';
import {shareReplay, map, mergeMap, switchMap, take} from 'rxjs/operators';
import {Injectable, inject} from '@angular/core';
import {HttpParams, HttpHeaders} from '@angular/common/http';
import {NavigationBarConfiguration} from './navigationbarconfiguration';
import {User} from '../currentuser/user';
import {CurrentUserService} from '../currentuser/currentuser.service';
import {FilterResult} from '../../resource/filterresult';
import {BaseApiService} from '../../core/services/base-api.service';
import {SupportedLocaleConfigurationService} from './supportedlocaleconfiguration.service';
import {Endpoint} from '../../layout/endpoint';

@Injectable()
export class NavigationBarConfigurationService extends BaseApiService {
  private countObservables: Observable<number>[] = [];
  private localeService = inject(SupportedLocaleConfigurationService);

  constructor(private currentUserService: CurrentUserService) {
    super();
  }

  public getNavigationBarConfigurations(): Observable<NavigationBarConfiguration[]> {
    const attributes = [
      this.getAttr('DisplayName'),
      this.getAttr('Description'),
      this.getAttr('UsageKeyword'),
      this.getAttr('CountXPath'),
      this.getAttr('UrlID'),
      this.getAttr('ParentOrder'),
      this.getAttr('Order'),
      this.getAttr('Icon'),
      this.getAttr('DisplayInFooter'),
      this.getAttr('SiteType'),
      this.getAttr('LinkedObject'),
      this.getAttr('ActionBarResources'),
    ].filter(attr => attr !== null);

    return this.getWithLocale<FilterResult>(
      '/' + this.getType('NavigationBarConfiguration'),
      attributes,
      10000,
      this.localeService.getSupportedLocaleConfiguration(),
    ).pipe(map(response => this.toNavigationBarConfigurationsW(response.Results)));
  }

  private toNavigationBarConfigurationsW(navbars: any): NavigationBarConfiguration[] {
    let finalNavbars: NavigationBarConfiguration[] = [];
    navbars.forEach(obj => {
      finalNavbars.push(this.toNavigationBarConfiguration(obj));
    });
    return finalNavbars;
  }

  private toSingleNavigationBarConfiguration(navbars: any): NavigationBarConfiguration {
    if (Array.isArray(navbars) && navbars.length == 1) {
      return this.toNavigationBarConfiguration(navbars[0]);
    } else {
      console.error(
        'NavigationBarConfigurationService::toSingleNavigationBarConfiguration invalid attribute given.',
      );
      return null;
    }
  }

  private toNavigationBarConfiguration(r: any): NavigationBarConfiguration {
    let navBarConf = <NavigationBarConfiguration>{
      id: r[this.getAttr('ObjectID')],
      displayname: r[this.getAttr('DisplayName')],
      description: r[this.getAttr('Description')],
      order: parseInt(r[this.getAttr('Order')]),
      parentorder: parseInt(r[this.getAttr('ParentOrder')]),
      usagekeyword: r[this.getAttr('UsageKeyword')],
      countxpath: r[this.getAttr('CountXPath')],
      defaultsearchscope: r[this.getAttr('LinkedObject')],
      urlid: r[this.getAttr('UrlID')],
      icon: r[this.getAttr('Icon')],
      count: null,
      displayinfooter: r[this.getAttr('DisplayInFooter')],
      sitetype: r[this.getAttr('SiteType')],
      linkedobject: r[this.getAttr('LinkedObject')],
      actionbarresources: r[this.getAttr('ActionBarResources')],
    };
    if (r[this.getAttr('CountXPath')] != null) {
      if (this.countObservables[r[this.getAttr('CountXPath')]] == null) {
        let filter = r[this.getAttr('CountXPath')];
        let obs: Observable<number>;
        if (filter.includes('%LoginID%') || filter.includes('%ObjectID%')) {
          let headers = new HttpHeaders({'Content-Type': 'application/json'});

          //Replace %LoginID% with ObjectID of Current User
          let user = this.currentUserService.getCurrentUser();
          obs = user.pipe(
            mergeMap((usr: User) => {
              filter = filter.replace(/%LoginID%/gi, usr.id);
              filter = filter.replace(/%ObjectID%/gi, usr.id);
              return this.languageService
                .getLanguage(this.localeService.getSupportedLocaleConfiguration())
                .pipe(
                  mergeMap((language: string) => {
                    let params = new HttpParams()
                      .append('filter', filter)
                      .append('attributes', this.getAttr('ObjectID'))
                      .append('pageSize', '1')
                      .append('locale', language);

                    return this.layoutService.endpoint$.pipe(
                      take(1),
                      switchMap((env: Endpoint) => {
                        const options = {
                          headers: this.headers,
                          params: params,
                          ...(!env.isCloud() ? {withCredentials: true} : {}),
                        };
                        return this.http.get<FilterResult>(env.getUrl() + 'v2/resources/', options);
                      }),
                      map((response: FilterResult) => {
                        return response.TotalCount;
                      }),
                    );
                  }),
                );
            }),
            shareReplay(1),
          );
        } else {
          let headers = new HttpHeaders({'Content-Type': 'application/json'});
          obs = this.languageService
            .getLanguage(this.localeService.getSupportedLocaleConfiguration())
            .pipe(
              mergeMap((language: string) => {
                let params = new HttpParams()
                  .append('filter', filter)
                  .append('attributes', this.getAttr('ObjectID'))
                  .append('pageSize', '1')
                  .append('locale', language);

                return this.layoutService.endpoint$.pipe(
                  take(1),
                  switchMap((env: Endpoint) => {
                    const options = {
                      headers: this.headers,
                      params: params,
                      ...(!env.isCloud() ? {withCredentials: true} : {}),
                    };
                    return this.http.get<FilterResult>(env.getUrl() + 'v2/resources/', options);
                  }),
                  map((response: FilterResult) => {
                    return response.TotalCount;
                  }),
                );
              }),
              shareReplay(1),
            );
        }
        this.countObservables[r[this.getAttr('CountXPath')]] = obs;
        navBarConf.count = obs;
      } else {
        navBarConf.count = this.countObservables[r[this.getAttr('CountXPath')]];
      }
    }
    return navBarConf;
  }
}
